import * as React from 'react';
import { PersistentStorageManager } from '../../../../../models/Storage';

export type ColumnId =
	| 'name'
	| 'tags'
	| 'contactInfo'
	| 'creationDate'
	| 'jobTitle'
	| 'birthday'
	| 'anniversary'
	| 'age'
	| 'contactOwner'
	| 'lastCommunicationDate';

export interface ColumnDefinition {
	id: ColumnId;
	label: string;
	isRequired: boolean;
	isVisible: boolean;
}

const DEFAULT_COLUMNS: ColumnDefinition[] = [
	{ id: 'name', label: 'Name', isRequired: true, isVisible: true },
	{ id: 'tags', label: 'Tags', isRequired: false, isVisible: true },
	{ id: 'contactInfo', label: 'Contact Info', isRequired: false, isVisible: true },
	{ id: 'creationDate', label: 'Creation Date', isRequired: false, isVisible: true },
	{ id: 'jobTitle', label: 'Job Title', isRequired: false, isVisible: false },
	{ id: 'birthday', label: 'Birthday', isRequired: false, isVisible: false },
	{ id: 'anniversary', label: 'Anniversary', isRequired: false, isVisible: false },
	{ id: 'age', label: 'Age', isRequired: false, isVisible: false },
	{ id: 'contactOwner', label: 'Contact Owner', isRequired: false, isVisible: false },
	{ id: 'lastCommunicationDate', label: 'Last Communication', isRequired: false, isVisible: false },
];

const TABLE_COLUMNS_STORAGE_KEY = 'contacts-table-column-visibility';
const MAX_VISIBLE_COLUMNS = 8;

export const useTableColumnVisibility = () => {
	const [columns, setColumns] = React.useState<ColumnDefinition[]>(() => {
		try {
			const stored = PersistentStorageManager.local.get(TABLE_COLUMNS_STORAGE_KEY);
			if (stored) {
				const storedSettings = JSON.parse(stored) as { id: ColumnId; isVisible: boolean }[];
				return DEFAULT_COLUMNS.map(defaultColumn => {
					const storedSetting = storedSettings.find(setting => setting.id === defaultColumn.id);
					return storedSetting ? { ...defaultColumn, isVisible: storedSetting.isVisible } : defaultColumn;
				});
			}
		} catch (error) {
			console.warn('Failed to parse stored column visibility preferences:', error);
		}
		return DEFAULT_COLUMNS;
	});

	const visibleColumns = React.useMemo(() => columns.filter(col => col.isVisible), [columns]);
	const visibleColumnCount = visibleColumns.length;
	const canAddMoreColumns = visibleColumnCount < MAX_VISIBLE_COLUMNS;

	const updateColumnVisibility = React.useCallback((columnId: ColumnId, isVisible: boolean) => {
		setColumns(prevColumns => {
			// Calculate if we can add more columns based on current state
			const currentVisibleCount = prevColumns.filter(col => col.isVisible).length;
			const canAddMore = currentVisibleCount < MAX_VISIBLE_COLUMNS;

			const updatedColumns = prevColumns.map(col => {
				if (col.id === columnId) {
					// Don't allow hiding required columns
					if (col.isRequired && !isVisible) {
						return col;
					}
					// Don't allow showing more columns if at max
					if (!col.isVisible && isVisible && !canAddMore) {
						return col;
					}
					return { ...col, isVisible };
				}
				return col;
			});

			// Save to localStorage
			try {
				const visibilitySettings = updatedColumns.map(({ id, isVisible: visible }) => ({ id, isVisible: visible }));
				PersistentStorageManager.local.setObject(TABLE_COLUMNS_STORAGE_KEY, visibilitySettings);
			} catch (error) {
				console.warn('Failed to save column visibility preferences:', error);
			}

			return updatedColumns;
		});
	}, []);

	const resetToDefaults = React.useCallback(() => {
		setColumns(DEFAULT_COLUMNS);
		PersistentStorageManager.local.remove(TABLE_COLUMNS_STORAGE_KEY);
	}, []);

	return {
		columns,
		visibleColumns,
		visibleColumnCount,
		maxVisibleColumns: MAX_VISIBLE_COLUMNS,
		canAddMoreColumns,
		updateColumnVisibility,
		resetToDefaults,
	};
};
